import { type HTMLAttributes } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  color?: string;
  thickness?: number;
}

const sizeStyles: Record<SpinnerSize, { dimension: number; thickness: number }> = {
  xs: { dimension: 16, thickness: 2 },
  sm: { dimension: 20, thickness: 2 },
  md: { dimension: 32, thickness: 3 },
  lg: { dimension: 48, thickness: 4 },
  xl: { dimension: 64, thickness: 4 },
};

export const Spinner = ({
  size = 'md',
  color = '#0F6CBD',
  thickness,
  className,
  ...props
}: SpinnerProps) => {
  const { dimension, thickness: defaultThickness } = sizeStyles[size];
  const strokeWidth = thickness ?? defaultThickness;

  return (
    <div
      className={cn('inline-flex items-center justify-center', className)}
      role="status"
      aria-label="Loading"
      {...props}
    >
      <motion.svg
        width={dimension}
        height={dimension}
        viewBox={`0 0 ${dimension} ${dimension}`}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
      >
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={(dimension - strokeWidth) / 2}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="opacity-20"
        />
        <motion.circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={(dimension - strokeWidth) / 2}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${Math.PI * (dimension - strokeWidth) * 0.75} ${Math.PI * (dimension - strokeWidth)}`}
        />
      </motion.svg>
    </div>
  );
};

// Loading Overlay - for covering content during load
interface LoadingOverlayProps extends HTMLAttributes<HTMLDivElement> {
  isLoading: boolean;
  text?: string;
  spinnerSize?: SpinnerSize;
}

export const LoadingOverlay = ({
  isLoading,
  text,
  spinnerSize = 'lg',
  className,
  children,
  ...props
}: LoadingOverlayProps) => {
  if (!isLoading) return <>{children}</>;

  return (
    <div className={cn('relative', className)} {...props}>
      <div className="opacity-30 pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm rounded-xl">
        <Spinner size={spinnerSize} />
        {text && <p className="mt-3 text-sm text-gray-500">{text}</p>}
      </div>
    </div>
  );
};

// Skeleton Loading - for placeholder content
interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton = ({
  variant = 'text',
  width,
  height,
  animation = 'pulse',
  className,
  ...props
}: SkeletonProps) => {
  const variantStyles = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const animationStyles = {
    pulse: 'animate-pulse',
    wave: 'shimmer',
    none: '',
  };

  return (
    <div
      className={cn(
        'bg-gray-200',
        variantStyles[variant],
        animationStyles[animation],
        className
      )}
      style={{
        width: width,
        height: height ?? (variant === 'text' ? undefined : '100px'),
      }}
      {...props}
    />
  );
};

export default Spinner;
